/************************************************/
/*          About dayjs                         */
/************************************************/
// The MIT License (MIT)
// 
// Copyright (c) 2018-present, iamkun
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

/************************************************/
/*          About noUiSlider                    */
/************************************************/
// The MIT License (MIT)
// 
// Copyright (c) 2019 Léon Gersen
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), 
// to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, 
// and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, 
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, 
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import dayjs from 'dayjs';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import './TimeLine.css';
import { GraphMediator } from './GraphLibrary';
import c from '../config';

const TEN_SECONDS_MS = 10000;
const ONE_SECOND_MS = 1000;
const TIME_DIGITS = 13;

const CURSOR_ICON = {
  [c.CURSOR.A]: '\u24B6',
  [c.CURSOR.B]: '\u24B7',
};

const ICON = {
  MARKER: '<svg xmlns="http://www.w3.org/2000/svg" height="15px" viewBox="0 0 24 24" width="15px" fill="#000000"><path d="M0 0h24v24H0z" fill="none"/><path d="M0 0h24v24H0z" fill="none"/><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>',
  CURRENT_POSITION: '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1f1f1f"><path d="M331.93-183.87v-171.59H135.87L480-776.13l344.13 420.67H628.07v171.59H331.93Zm83-83h130.14v-171.35h109.34L480-640.24 305.59-438.22h109.34v171.35ZM480-438.22Z"/></svg>',
};

// timeline上に引く線の設定
// 現在時間から動的に位置を調整するため、幅(px)はcssではなくjsで保持
const LINE_SETTINGS = {
  CURRENT: {
    WIDTH: 3,
    CLASS: 'timeline-current-line',
  }
};

export class TimeLine extends HTMLElement {
  static staticMarkerInstIndex = 0;

  constructor() {
    super();
    // 開始時間
    this._id = null;
    // 開始時間のデフォルト値
    this._timelineComponent = '';
    // ステップ値（小）デフォルトでは1秒(1000ms)
    this.stepSizeSmall = ONE_SECOND_MS;
    // ステップ値（大）デフォルトでは10秒(10000ms)
    this.stepSizeLarge = TEN_SECONDS_MS;
    // 全体の時間
    this.totalTimeMs = 0;
    this.originTotalTimeMs = 0;
    this.sliderMax = 1000;
    // マーカハッシュ
    this.markerHashArray = [];
    this.markerElement = '';
    this.index = 0;
    // スライダーレンジの最大/最小
    this.rangeMaxMs = 0;
    this.rangeMinMs = 0;
    // スライダーの開始時間/現在時間/終了時間
    this._startTimeMs = null;
    this.currentTimeMs = 0;
    this._endTimeMs = null;
    // スライダーのmax/minのデータ保持用
    this.prevRangeMaxMs = 0;
    this.prevRangeMinMs = 0;
    // 左右の範囲つまみの有無(true:無し)(CITS)
    this._noRange = false;
    // 範囲のみ指定パターン(true:範囲のみ)
    this._rangeOnly = false;
    // 非表示対応
    this._disable = false;
    // カレント位置更新コールバック
    this._handleChange = null;
    // インスタンス固有のインデックス。マーカー・カーソルのIDが、タイムラインごとに重複しないようにする
    this.markerInstIndex = TimeLine.staticMarkerInstIndex;
    TimeLine.staticMarkerInstIndex++;
    this.dataAppending = false; // データ投入中フラグ
    // 基準時刻
    this.baseTime = 0;
  }

  close() {
    // 描画済みのマーカーの星印を全て削除
    for (let i = 0; i < this.index; i++) {
      const marker = document.querySelector(`marker_${this.markerInstIndex}_${i}`);
      marker && marker.remove();
    }
    this.deleteCurrentPositionIcon();
    this.deleteCurrentPositionLine();
    // マーカハッシュ
    this.markerHashArray = [];
    this.markerElement = '';
    this.index = 0;
    this.initTime();
    this.baseTime = 0;
  }

  // 監視する属性名のリストを返却する
  static get observedAttributes() {
    return ['id', 'start-time', 'end-time', 'no-range', 'range-only', 'disable']; // 左右の範囲つまみの有無指定を追加(CITS)
  }

  // 桁数を返すメソッド
  getDigits(number) {
    return Math.floor(Math.log10(number)) + 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    oldValue;

    switch (name) {
      case "id":
        this._id = newValue;
        break;

      case "start-time":
        if (newValue) {
          this.rangeMinMs = parseInt(newValue, 10); // 10進数指定を追加(CITS)
          this._startTimeMs = parseInt(newValue, 10); // 10進数指定を追加(CITS)
        }
        break;

      case "end-time":
        if (newValue) {
          this.rangeMaxMs = parseInt(newValue, 10); // 10進数指定を追加(CITS)
          this._endTimeMs = parseInt(newValue, 10); // 10進数指定を追加(CITS)
          this.totalTimeMs = this._endTimeMs - this._startTimeMs;
          this.originTotalTimeMs = this._endTimeMs - this._startTimeMs;
        }
        break;

      // 1本のつまみのパターン追加(CITS)
      case "no-range":
        this._noRange = newValue === "true";
        break;

      case "range-only":
        this._rangeOnly = newValue === "true";
        break;

      case "disable":
        this._disable = newValue;
        break;

      default:
        break;
    }
  }


  validation() {
    if (!this._id) {
      throw new Error('id does not exist');
    }
    if (typeof this._id !== 'string') {
      throw new Error('id must be of type string');
    }
    // 開始時間の桁数/型チェック
    if (this._startTimeMs && this.getDigits(this._startTimeMs) !== TIME_DIGITS) {
      throw new Error('start-time has a different number of digits');
    }
    if (this._startTimeMs && typeof this._startTimeMs !== 'number') {
      throw new Error('start-time must be of type number');
    }
    // 終了時間の桁数チェック
    if (this._endTimeMs && this.getDigits(this._endTimeMs) !== TIME_DIGITS) {
      throw new Error('end-time has a different number of digits');
    }
    if (this._endTimeMs && typeof this._endTimeMs !== 'number') {
      throw new Error('end-time must be of type number');
    }
  }

  // コンポーネント設定
  setComponent() {
    if (this._timelineComponent) {
      return;
    }
    // タイムラインを生成する
    const timelineComponent = document.createElement('div');
    timelineComponent.setAttribute("id", "timelineDiv");
    timelineComponent.setAttribute("data-testid", "timelineDiv"); //UT向けコード埋め込み

    /* タイムラインのサイズと位置を最適化(CITS) */
    timelineComponent.style.width = "100%";
    timelineComponent.style.marginTop = "5px";
    timelineComponent.style.marginLeft = "40px";
    timelineComponent.style.height = "40px";
    timelineComponent.style.maxHeight = "40px";

    // タイムラインコンテイナーを生成する
    const timelineContainer = document.createElement("p");
    timelineContainer.setAttribute("align", "center");

    // スライダーを生成する
    const slider = document.createElement("div");
    slider.setAttribute("id", "slider");
    slider.setAttribute("data-testid", "slider"); //UT向けコード埋め込み

    // スライダーの初期値を設定する
    noUiSlider.create(slider, {
      start: this.getSliderStartValues(),
      connect: true, // 3本のつまみを線でつなぐ
      range: {
        'min': this.rangeMinMs,
        'max': this.rangeMaxMs
      },
      behaviour: 'none',
      tooltips: {
        to: (value) => {
          return GraphMediator.getPassedTimeStrFromBaseTime(this.baseTime, value, true);
        }
      }
    });

    // スライダーのスタイル（CSS要素）を設定する
    slider.style.webkitAppearance = "none";
    slider.style.appearance = "none";
    slider.style.cursor = "pointer";
    slider.style.height = "20px";
    slider.style.width = "100%";
    slider.style.border = "solid 3px #000000";
    if (this._disable) {
      slider.noUiSlider.disable();
    }

    timelineComponent.appendChild(timelineContainer);
    timelineContainer.appendChild(slider);
    this.parentElement.appendChild(timelineComponent);
    this._timelineComponent = slider;
  }

  // 基準時間を更新する
  setBaseTime(baseTime) {
    this.baseTime = baseTime;
  }

  // 各種時間を設定する
  initTime() {
    this.currentTimeMs = this._startTimeMs;
  }

  /**
   * タイムラインのコンポーネントを取得する
   * @returns {HTMLElement} タイムラインのコンポーネント
   */
  getComponent() {
    return this._timelineComponent;
  }

  /**
   * マーカーのDOM要素を取得する
   * @returns {HTMLElement} マーカーのDOM要素
   */
  getMarkerElement() {
    return this.markerElement;
  }

  /**
   * カーソルのIDを取得する
   * @param {string} cursorType カーソル種別
   * @returns {string} カーソルのID
   */
  getCursorId(cursorType) {
    return `cursor_${this.markerInstIndex}_${cursorType}`;
  }

  /**
   * 現在位置アイコンのIDを取得する
   * @returns {string} 現在位置アイコンのID
   */
  getCurrentPositionIconId() {
    return `current_position_${this.markerInstIndex}`;
  }

  /**
   * 種類ごとにスライダーの初期値を取得する
   * @param {number} options.currentTime 現在の時間(ms)
   * @param {number} options.startTime 開始時間(ms)
   * @param {number} options.endTime 終了時間(ms)
   * @returns {Array<number>} スライダーの初期値
   */
  getSliderStartValues({ currentTime = this.currentTimeMs, startTime = this._startTimeMs, endTime = this._endTimeMs } = {}) {
    if (this._noRange) {
      return [currentTime];
    }
    if (this._rangeOnly) {
      return [startTime, endTime];
    }
    return [startTime, currentTime, endTime];
  }

  /**
   * スライダーの開始つまみのハンドル番号を取得する
   * @returns {number|null} 開始つまみのハンドル番号。開始つまみがない場合はnullを返す
   */
  getStartKnobNumber() {
    return this._noRange ? null : 0;
  }

  /**
   * スライダーの現在位置つまみのハンドル番号を取得する
   * @returns {number|null} 現在位置つまみのハンドル番号。現在位置つまみがない場合はnullを返す
   */
  getCurrentKnobNumber() {
    if (this._rangeOnly) {
      return null;
    }
    return this._noRange ? 0 : 1;
  }

  /**
   * スライダーの終了つまみのハンドル番号を取得する
   * @returns {number|null} 終了つまみのハンドル番号。終了つまみがない場合はnullを返す
   */
  getEndKnobNumber() {
    if (this._noRange) {
      return null;
    }
    return this._rangeOnly ? 1 : 2;
  }

  /**
   * アイコン描画共通処理
   * @param {string} id 追加するDOMのID
   * @param {number} currentTimeMs 現在地の時間(ms)
   * @param {string} iconHtml アイコンのHTML文字列
   */
  createIconElement(id, currentTimeMs, iconHtml) {
    if (!this.baseTime) return '';
    // 現在位置のつまみのサイズ取得(CITS)
    const handle = document.querySelector('.noUi-handle');
    const handleWidth = parseInt(window.getComputedStyle(handle).getPropertyValue('width'), 10);

    // アイコンの座標（px）
    // ピクセル(this._timelineComponent.clientWidth)に対するUNIX時間の幅は(this.rangeMaxMs - this.rangeMinMs)
    const markerRect = this.calcTimelinePositionPx(currentTimeMs, handleWidth); // 現在位置のつまみの中心にマーカを表示するように修正(CITS)

    // アイコンの作成
    const newElement = document.createElement(id);
    newElement.innerHTML = iconHtml;
    newElement.style.position = "absolute";
    newElement.style.width = `${handleWidth}px`; // アイコンのサイズと現在位置のつまみのサイズを同一に修正(CITS)
    newElement.style.left = `${markerRect}px`; // アイコンの描画位置
    newElement.style.display = this.dataAppending ? "none" : "inline"; // データ投入中の場合は表示しない
    return newElement;
  }

  setMarker(time = null, memo = null, uuid = null, tag = null) {
    if (!this.baseTime) return;
    let currentTime = time;

    if (!currentTime) {
      currentTime = this.currentTimeMs;
    }

    // マーカ存在フラグ
    let markerExists = false;

    // 同じuuidのマーカが存在する場合はメモとタグ情報を上書き
    this.markerHashArray = this.markerHashArray.map(marker => {
      const key = Object.keys(marker)[0];
      if (marker[key].markerUuid === uuid) {
        markerExists = true;
        return {
          [key]: {
            ...marker[key],
            markerMemo: memo,
            markerTag: tag
          }
        };
      }
      return marker;
    });

    // 同じuuidのマーカが存在しない場合は新規登録
    if (!markerExists) {
      this.markerHashArray.push({
        [`marker_${this.markerInstIndex}_${this.index}`]: {
          markerTime: dayjs(currentTime).valueOf(),
          markerMemo: memo,
          markerUuid: uuid,
          markerTag: tag
        }
      });
    }
    // 同じuuidのマーカが存在する場合は以降の処理を打ち切り
    else {
      return;
    }

    // unix時間をms時間に変換
    const currentTimeNotUnix = currentTime - this.rangeMinMs;

    const markerIcon = ICON.MARKER; // 星の文字列をsvgに変更
    this.markerElement = this.createIconElement(`marker_${this.markerInstIndex}_${this.index}`, currentTimeNotUnix, markerIcon);
    this.markerElement.setAttribute("markertime", currentTimeNotUnix);
    this.markerElement.setAttribute("markeruuid", uuid);
    this.markerElement.setAttribute("class", "timeline-marker");
    this.index += 1;

    this._timelineComponent.appendChild(this.markerElement);
  }

  /**
   * アイコン更新共通処理
   */
  updateIcon(id, timeAttributeName, handleWidth) {
    if (!this.baseTime) return;
    const markerElement = document.querySelector(id);
    if (!markerElement) return;

    const markerTime = markerElement.getAttribute(timeAttributeName);
    const markerRect = this.calcTimelinePositionPx(Number(markerTime), handleWidth); // 現在位置のつまみの中心にマーカを表示するように修正(CITS)
    markerElement.style.left = `${markerRect}px`; // マーカーの描画位置
    markerElement.style.display = "inline";
  }

  // マーカの描画位置を調整
  updateMarker(index = null) {
    if (!this.baseTime) return;
    // データ投入中の場合は以降の処理をしない
    if (this.dataAppending) {
      return;
    }

    // 現在位置のつまみのサイズ取得(CITS)
    const handle = document.querySelector('.noUi-handle');
    if (!handle) {
      return;
    }
    const handleWidth = parseInt(window.getComputedStyle(handle).getPropertyValue('width'), 10);

    const indexSupremum = (index !== null) ? index : this.index;

    if (this.markerElement && this.markerElement.style) {
      for (let i = 0; i < indexSupremum; i++) {
        // 非表示の場合があるため表示するように設定する
        this.markerElement.style.display = "inline";

        this.updateIcon(`marker_${this.markerInstIndex}_${i}`, "markertime", handleWidth);
      }
    }

    for (const cursorType of Object.values(c.CURSOR)) {
      this.updateIcon(`#${this.getCursorId(cursorType)}`, "cursortime", handleWidth);
    }

    this.syncCurrentPositionIcon();
  }

  /**
   * UUID指定でマーカーを削除する
   * @param {string} uuid 削除するマーカーのUUID
   */
  deleteMarker(uuid) {
    if (!this.baseTime) return;
    const element = document.querySelector(`[markeruuid='${uuid}']`);
    element.remove();
    this.markerHashArray = this.markerHashArray.filter(marker => Object.values(marker)[0].markerUuid !== uuid);
  }

  /**
   * カーソルアイコン追加
   * @param {string} cursorType カーソル種別
   * @param {number} time カーソルアイコンを設定する時間
   */
  setCursor(cursorType, time = null) {
    if (!this.baseTime) return;
    const elementId = this.getCursorId(cursorType);
    const currentTime = time || this.currentTimeMs;
    const cursorIcon = CURSOR_ICON[cursorType] || '';
    // unix時間をms時間に変換
    const currentTimeNotUnix = currentTime - this.rangeMinMs;

    const newCursorElement = this.createIconElement(elementId, currentTimeNotUnix, cursorIcon);
    newCursorElement.setAttribute("id", elementId);
    newCursorElement.setAttribute("class", "timeline-cursor-mark");
    newCursorElement.setAttribute("cursortime", currentTimeNotUnix);
    newCursorElement.style.cursor = "default"; // 親を継承してカーソルが選択用表示になるため、'default' で上書きする

    this._timelineComponent.appendChild(newCursorElement);
  }

  /**
   * カーソルアイコン削除
   * @param {string} cursorType カーソル種別
   */
  deleteCursor(cursorType) {
    if (!this.baseTime) return;
    const elementId = this.getCursorId(cursorType);
    const existCursor = document.querySelector(`#${elementId}`);
    if (existCursor) {
      // 削除
      existCursor.remove();
    }
  }

  /**
   * 現在地アイコンを表示するか否かを判定する
   * @returns {boolean} 現在地アイコンを表示するか否か
   */
  shouldDisplayCurrentIcon() {
    return this._rangeOnly && this.baseTime && this._timelineComponent;
  }

  /**
   * 現在地アイコンを同期する
   * @param {number} time 現在地アイコンを同期する時間
   */
  syncCurrentPositionIcon(time = this.currentTimeMs) {
    // データ投入中の場合は以降の処理をしない
    if (this.dataAppending) {
      return;
    }
    if (!this.shouldDisplayCurrentIcon()) {
      this.deleteCurrentPositionIcon();
      this.deleteCurrentPositionLine();
      return;
    }

    const elementId = this.getCurrentPositionIconId();
    const currentTimeNotUnix = time - this.rangeMinMs;
    const existElement = this._timelineComponent.querySelector(`#${elementId}`);

    if (!existElement) {
      const iconHtml = ICON.CURRENT_POSITION;
      const newElement = this.createIconElement(elementId, currentTimeNotUnix, iconHtml);
      newElement.setAttribute("id", elementId);
      newElement.setAttribute("class", "timeline-current-mark");
      newElement.setAttribute("current-time", currentTimeNotUnix);
      this._timelineComponent.appendChild(newElement);
      // 現在地アイコンを表示する場合は現在地ラインも表示する
      this.syncCurrentPositionLine(time);
      return;
    }

    existElement.setAttribute("current-time", currentTimeNotUnix);
    const handle = this._timelineComponent.querySelector('.noUi-handle');
    if (!handle) return;
    const handleWidth = parseInt(window.getComputedStyle(handle).getPropertyValue('width'), 10);
    this.updateIcon(`#${elementId}`, "current-time", handleWidth);
    // 現在地アイコンを更新する場合は現在地ラインも更新する
    this.syncCurrentPositionLine(time);
  }

  /**
   * 現在地アイコンを削除する
   */
  deleteCurrentPositionIcon() {
    const element = document.querySelector(`#${this.getCurrentPositionIconId()}`);
    if (element) {
      element.remove();
    }
  }

  /**
   * 現在地ラインのIDを取得する
   * @returns {string} 現在地ラインのID
   */
  getCurrentPositionLineId() {
    return `current_position_line_${this.markerInstIndex}`;
  }

  /**
   * タイムライン上の位置を計算する。アイコンの中心が現在地を指すように、アイコンの幅も考慮して位置を計算する
   * @param {number} elapsedMs 基準開始位置からの経過時間(ms)
   * @param {number} iconWidth アイコンの幅(px)
   * @returns {number} タイムライン上の位置(px)
   */
  calcTimelinePositionPx(elapsedMs, iconWidth = 0) {
    const range = this.rangeMaxMs - this.rangeMinMs;
    const positionRatio = range > elapsedMs ? elapsedMs / range : 1;
    return positionRatio * this._timelineComponent.clientWidth - (iconWidth / 2);
  }

  /**
   * 現在地ラインを同期する
   * @param {number} time 現在地ラインを同期する時間
   */
  syncCurrentPositionLine(time = this.currentTimeMs) {
    const elementId = this.getCurrentPositionLineId();
    const elapsedTime = time - this.rangeMinMs;
    const existElement = this._timelineComponent.querySelector(`#${elementId}`);
    // シークバーとつまみの間に現在地ラインを表示するため、スライダーのベース要素を取得してそこに現在地ラインを追加する
    const sliderBase = this._timelineComponent.querySelector('.noUi-base');

    if (!existElement) {
      const centerPx = this.calcTimelinePositionPx(elapsedTime, LINE_SETTINGS.CURRENT.WIDTH);
      const newElement = document.createElement('div');
      newElement.setAttribute("id", elementId);
      newElement.setAttribute("class", LINE_SETTINGS.CURRENT.CLASS);
      newElement.setAttribute("current-time", elapsedTime);
      newElement.style.width = `${LINE_SETTINGS.CURRENT.WIDTH}px`;
      newElement.style.left = `${centerPx}px`;
      sliderBase?.appendChild(newElement);
      return;
    }

    existElement.setAttribute("current-time", elapsedTime);
    const centerPx = this.calcTimelinePositionPx(elapsedTime, LINE_SETTINGS.CURRENT.WIDTH);
    existElement.style.left = `${centerPx}px`;
    existElement.style.display = "block";
  }

  /**
   * 現在地ラインを削除する
   */
  deleteCurrentPositionLine() {
    const element = document.querySelector(`#${this.getCurrentPositionLineId()}`);
    if (element) {
      element.remove();
    }
  }

  getMarkerHashArray() {
    return this.markerHashArray;
  }

  // newDataTimeMs = 新規追加されるデータの時間（ミリ秒）
  updateComponent(newDataTimeMs = null) {
    if (!this.baseTime) return;
    // 更新前のレンジ最大・最小を保持
    this.prevRangeMinMs = this.rangeMinMs;
    this.prevRangeMaxMs = this.rangeMaxMs;
    // 新規追加されるデータの時間がレンジの最大時間より大きい場合、レンジ最大の値を更新する
    if (newDataTimeMs > this.rangeMaxMs) {
      this.rangeMaxMs = newDataTimeMs;
    }
    // 終了時間が右端（最大）にある
    if (this._endTimeMs === this.prevRangeMaxMs) {
      this._endTimeMs = this.rangeMaxMs;
    }
    // 全体時間を更新
    this.totalTimeMs = this._endTimeMs - this._startTimeMs;

    this._timelineComponent.noUiSlider.updateOptions({
      range: {
        'min': this.rangeMinMs,
        'max': this.rangeMaxMs
      },
      start: this.getSliderStartValues(),
    });

    this.syncCurrentPositionIcon();
  }

  /**
   * 現在時刻のスライダーのつまみ位置を変更する
   * @param {number} newTime つまみ位置を更新する時間
   */
  setCurrentSliderPosition(newTime) {
    if (!this.baseTime) return;
    this.currentTimeMs = newTime;
    this.syncCurrentPositionIcon(newTime);
    if (this._rangeOnly) {
      return;
    }
    const newSettings = this.getSliderStartValues({ currentTime: newTime });
    this._timelineComponent.noUiSlider.set(newSettings);
  }

  /**
   * 表示範囲の開始・終了のスライダーのつまみ位置を変更する
   * @param {number} newStartTime 範囲開始つまみ位置を更新する時間
   * @param {number} newEndTime 範囲終了つまみ位置を更新する時間
   * @returns 
   */
  setRangeSliderPosition(newStartTime, newEndTime) {
    if (this._noRange || !this.baseTime) {
      return;
    }
    this._startTimeMs = newStartTime;
    this._endTimeMs = newEndTime;
    const newSettings = this.getSliderStartValues({ startTime: newStartTime, endTime: newEndTime, currentTime: this.currentTimeMs });
    this._timelineComponent.noUiSlider.set(newSettings);
  }
}
